import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { GokaiBanner } from "../components/GokaiBanner";
import { FlowerOfLife, ChokuRei } from "../components/SacredGeometry";
import { Sparkles, Flame, Sun, Compass, Scroll, HeartHandshake, Feather, ArrowRight } from "lucide-react";

const ICONS = { Sparkles, Flame, Sun, Compass };

const HERO_IMG = "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Landing() {
  const [courses, setCourses] = useState([]);
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

  return (
    <div className="bg-sanctuary-deep overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Meditation at dawn" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-sanctuary-deep/70 via-sanctuary-deep/85 to-sanctuary-deep" />
        </div>
        <div className="absolute top-1/4 right-[8%] w-[32rem] h-[32rem] aura breathe rounded-full pointer-events-none" />
        <FlowerOfLife className="absolute -right-32 top-40 w-[36rem] text-gold/10 spin-slow pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <motion.div initial="hidden" animate="show" className="max-w-2xl">
            <motion.div variants={fadeUp} custom={0} className="eyebrow mb-6 flex items-center gap-3">
              <span className="font-jp text-gold text-base">臼井靈氣療法</span>
              <span className="w-8 h-px bg-gold/40" /> The Usui System of Natural Healing
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-parchment">
              Learn the sacred art of <span className="italic text-gold">Reiki</span>, from first light to mastery
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-8 text-lg text-text-muted leading-relaxed max-w-xl">
              A guided journey through all four degrees of traditional Reiki — Shoden, Okuden, Shinpiden, and the Master path — offered as a living practice, not a course to be rushed.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-10 flex flex-wrap gap-4">
              <Link to="/membership" className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors" data-testid="hero-begin-button">
                Begin your journey
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/courses" className="inline-flex items-center gap-2 px-8 py-4 rounded-full gold-border text-parchment hover:bg-gold/10 transition-colors" data-testid="hero-explore-button">
                Explore the path
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* GOKAI */}
      <section className="relative py-24 border-y border-gold/12 bg-sanctuary-surface grain">
        <ChokuRei className="absolute left-1/2 -translate-x-1/2 -top-8 w-16 text-gold/30" />
        <div className="max-w-4xl mx-auto px-6">
          <div className="eyebrow text-center mb-8">The Five Precepts · Gokai</div>
          <GokaiBanner />
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="py-28 max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-16">
          <div className="eyebrow mb-4">The Four Degrees</div>
          <h2 className="font-display text-4xl sm:text-5xl text-parchment font-light">A path walked in four sacred steps</h2>
          <p className="mt-5 text-text-muted leading-relaxed">Each degree opens new channels and deepens your connection to the universal life force. Progress with patience — the energy meets you where you are.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {courses.map((c, i) => {
            const Icon = ICONS[c.icon] || Sparkles;
            return (
              <motion.div key={c.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <Link to={`/courses/${c.id}`} className="group block h-full relative rounded-2xl bg-sanctuary-card gold-border p-7 hover:border-gold/40 transition-colors duration-500" data-testid={`landing-course-${i + 1}`}>
                  <div className="absolute top-5 right-6 font-jp text-3xl text-gold/20 group-hover:text-gold/40 transition-colors">{c.japanese_name}</div>
                  <div className="w-12 h-12 rounded-full bg-gold/10 grid place-items-center text-gold mb-6 group-hover:bg-gold/20 transition-colors">
                    <Icon size={22} />
                  </div>
                  <div className="eyebrow mb-2">{c.level_label}</div>
                  <h3 className="font-display text-2xl text-parchment mb-2">{c.title}</h3>
                  <p className="text-sm text-text-muted leading-relaxed line-clamp-3">{c.description}</p>
                  <div className="mt-6 flex items-center gap-2 text-gold text-sm">
                    {c.lesson_count} teachings <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* LINEAGE */}
      <section className="relative py-28 bg-sanctuary-surface border-y border-gold/12">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Mount Kurama cedar forest" className="rounded-2xl gold-border object-cover w-full h-[26rem]" />
            <div className="absolute -bottom-6 -right-6 bg-sanctuary-deep gold-border rounded-2xl px-6 py-5 gold-glow">
              <div className="font-jp text-gold text-2xl">鞍馬山</div>
              <div className="text-xs text-text-muted mt-1">Mount Kurama · 1922</div>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-4">An Unbroken Lineage</div>
            <h2 className="font-display text-4xl text-parchment font-light mb-6">Carried heart to heart, since Mikao Usui</h2>
            <p className="text-text-muted leading-relaxed mb-8">After twenty-one days of meditation and fasting upon Mount Kurama, Mikao Usui received the gift of Reiki. From his hands it passed to Chujiro Hayashi, then to Hawayo Takata, and onward to the teachers of today. When you learn here, you join this living stream.</p>
            <div className="flex flex-col gap-4">
              {[
                { name: "Mikao Usui", role: "Founder · 臼井甕男", icon: Sun },
                { name: "Chujiro Hayashi", role: "Second in lineage · 林忠次郎", icon: Flame },
                { name: "Hawayo Takata", role: "Brought Reiki West · 高田はわよ", icon: HeartHandshake },
              ].map((p) => (
                <div key={p.name} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 grid place-items-center text-gold"><p.icon size={16} /></div>
                  <div>
                    <div className="text-parchment">{p.name}</div>
                    <div className="text-xs text-text-muted">{p.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="py-28 max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="eyebrow mb-4">What awaits within</div>
          <h2 className="font-display text-4xl sm:text-5xl text-parchment font-light">Everything you need to practise</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Feather, t: "Guided Transmissions", d: "Video teachings and meditations to accompany every stage of your attunement." },
            { icon: Scroll, t: "Sacred Study Scrolls", d: "Downloadable PDF guides, hand-position charts and symbol references to keep." },
            { icon: Sparkles, t: "Attunement Ceremonies", d: "Mindful reflection quizzes and printable certificates to honour each degree." },
          ].map((f, i) => (
            <motion.div key={f.t} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} className="rounded-2xl bg-sanctuary-card gold-border p-8">
              <div className="w-12 h-12 rounded-full bg-gold/10 grid place-items-center text-gold mb-6"><f.icon size={22} /></div>
              <h3 className="font-display text-2xl text-parchment mb-3">{f.t}</h3>
              <p className="text-text-muted text-sm leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div className="absolute inset-0 aura breathe pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <FlowerOfLife className="w-20 mx-auto text-gold/40 mb-8" />
          <h2 className="font-display text-4xl sm:text-5xl text-parchment font-light">Your hands already carry this light</h2>
          <p className="mt-6 text-text-muted leading-relaxed">Become a member and receive full access to every degree, from Shoden through Master.</p>
          <Link to="/membership" className="mt-10 inline-flex items-center gap-2 px-9 py-4 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors" data-testid="cta-join-button">
            Join the Sanctuary <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
